import React from 'react';
import { Phone, Mail, MapPin, Clock, Award, ShieldCheck } from 'lucide-react';
import ContactForm from '../components/ContactForm';
import { contactInfo } from '../data';
import SEO from '../components/SEO';

export default function Kontakt() {
  return (
    <div className="pt-24 md:pt-40 xl:pt-28 pb-16">
      <SEO
        title="Kontakt Os | Få et Uforpligtende Tilbud | Stenbelægning.dk"
        description="Kontakt os i dag for et uforpligtende tilbud og faglig rådgivning om helstøbt stenbelægning. Vi rådgiver om materialer, farver og udførelse i hele Danmark."
      />
      {/* Header Section */}
      <section className="bg-stone-50 py-16 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-stone-900 tracking-tight mt-2">
              Få et uforpligtende tilbud
            </h1>
            <p className="text-xl text-stone-600 mt-4 font-medium">
              Vi rådgiver om muligheder, farver, opbygning og vedligeholdelse
            </p>
            <p className="text-stone-500 mt-4 leading-relaxed">
              Kontakt os for en vurdering af dit projekt. Vi står klar til at hjælpe dig med at finde den helt rigtige løsning til din indkørsel, terrasse, trappe eller gangsti.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info Column */}
            <div className="space-y-8 lg:col-span-1">
              <div>
                <h2 className="text-2xl font-bold text-stone-900 mb-6">Direkte kontakt</h2>
                <ul className="divide-y divide-stone-200">
                  {/* Phone */}
                  <li className="flex items-start gap-4 py-5 first:pt-0 last:pb-0">
                    <Phone className="h-6 w-6 text-stone-900 shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-stone-900">Ring til os</h4>
                      <a 
                        href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`} 
                        className="text-stone-600 hover:text-emerald-600 font-medium mt-1 block transition-colors"
                      >
                        {contactInfo.phone}
                      </a>
                      <p className="text-xs text-stone-400 mt-1">Hverdage 08:00 - 16:00</p>
                    </div>
                  </li>

                  {/* Email */}
                  <li className="flex items-start gap-4 py-5 first:pt-0 last:pb-0">
                    <Mail className="h-6 w-6 text-stone-900 shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-stone-900">Skriv en e-mail</h4>
                      <a 
                        href={`mailto:${contactInfo.email}`} 
                        className="text-stone-600 hover:text-emerald-600 font-medium mt-1 block transition-colors break-all"
                      >
                        {contactInfo.email}
                      </a>
                      <p className="text-xs text-stone-400 mt-1">Vi svarer inden for 24 timer</p>
                    </div>
                  </li>

                  {/* Address */}
                  <li className="flex items-start gap-4 py-5 first:pt-0 last:pb-0">
                    <MapPin className="h-6 w-6 text-stone-900 shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-stone-900">Adresse</h4>
                      <p className="text-stone-600 mt-1">{contactInfo.address}</p>
                      {contactInfo.cvr && (
                        <p className="text-xs text-stone-400 mt-1">CVR: {contactInfo.cvr}</p>
                      )}
                    </div>
                  </li>
                </ul>
              </div>

              {/* Opening Hours Card */}
              <div className="bg-stone-50 rounded-2xl p-6 border border-stone-100">
                <h3 className="font-bold text-stone-900 mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-stone-900" />
                  Åbningstider
                </h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  Vores telefoner og kontor er åbne alle hverdage. Vi kommer også gerne ud til besigtigelser i weekenden efter forudgående aftale.
                </p>
                <div className="mt-4 pt-4 border-t border-stone-200/60 text-sm text-stone-700 font-semibold">
                  {contactInfo.hours}
                </div>
              </div>

              {/* Guarantee Card */}
              <div className="bg-stone-50 border border-stone-200 rounded-2xl p-6">
                <h3 className="font-bold text-stone-900 mb-2 flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-stone-900" />
                  10 års garanti
                </h3>
                <p className="text-stone-700 text-sm leading-relaxed">
                  Alt vores arbejde er dækket af 10 års garanti. Det er din sikkerhed for en belægning, der holder farve, form og styrke i mange år fremover.
                </p>
              </div>
            </div>

            {/* Form Column */}
            <div className="lg:col-span-2">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
